import React from 'react';
import './AllArticles.css';
import GoToTop from "../Support/GoToTop"; // Global CSS import

const Icons = () => {
    return (
        <article className="article">
            <header className="header">
                <h1>A Biblical Defense of Icons</h1>
                <p className="author">By Jakob Puckett</p>
            </header>

            <section>
                <p>
                    The use and veneration of icons is an unmistakable part of Orthodox Christianity.
                    Sacred images – as “icon” simply means “image” – have a place in our worship because,
                    as God told Moses on the mountain, earthly worship is to reflect heavenly worship
                    (Exodus 25:9, Acts 7:44, Hebrews 8:5). Icons depict people and beings serving before God’s heavenly throne.
                </p>
                <p>
                    These visual elements of Orthodox worship can be foreign to those unfamiliar with the practice, and are often misunderstood. Some go so far as to accuse Orthodox Christians of violating God’s commandment against making graven images (Exodus 20:3-4), arguing that images have no place in worship.
                </p>
                <p>
                    However, such rejection of sacred images is not Biblical. Citing God’s commandment against idolatry misreads the text and ignores the many uses of images in Biblical worship. Moreover, the principle behind venerating icons reflects the unity of the Trinity.
                </p>
            </section>

            <section>
                <h2>Images in Biblical Worship</h2>
                <p>
                    God’s commandment is against serving images in idolatry. If it applied to all images, then the temple itself would be idolatrous. Yet Scripture describes vivid imagery in the temple: cherubim, bulls, lions, palm trees, and more (Exodus 25–26, 1 Kings 6–7).
                </p>
                <p>
                    Moses explains that the Israelites were not to make “forms” because they “saw no form” when God spoke at Sinai (Deut. 4:15–18). But once God revealed heavenly worship, he commanded Moses and Solomon to include images in worship spaces.
                </p>
                <p>
                    Ezekiel’s vision of the future temple also includes cherubim (Ezekiel 41:18–20). These images are not worshiped—they help us imitate heavenly worship.
                </p>
            </section>

            <section>
                <h2>Icons of Christ and the Saints</h2>
                <p>
                    The Israelites didn’t depict God because he hadn’t revealed his form. But through the incarnation, God has revealed himself. Jesus Christ is “the image of the invisible God” (Col. 1:15, Heb. 1:3), and “the glory of God in the face of Jesus Christ” (2 Cor. 4:4–6).
                </p>
                <p>
                    Because God has revealed his image, it is now appropriate to depict it. We also depict saints and angels who serve before God’s throne (Rev. 7:9–15, Heb. 12:1, 22–24).
                </p>
            </section>

            <section>
                <h2>Why We Honor Icons</h2>
                <p>
                    The honor given to an image passes to the one it depicts. To see Christ is to see the Father (John 14:9). To honor Christ’s image is to honor the Father.
                </p>
                <p>
                    This principle appears throughout Scripture: honoring Christ’s brothers is honoring Christ (Matt. 25:31–46), obeying authority is obeying God (Rom. 13:1), and honoring saints honors Christ (2 Cor. 3:18, Col. 3:9–10).
                </p>
                <p>
                    The saints are alive in Christ (Luke 20:38), and honoring their image—through bowing or kissing—is a Biblical practice.
                </p>
            </section>

            <section>
                <h2>Scriptural Support for Veneration</h2>
                <p>
                    Scripture approves honoring objects for what they represent. People bowed before the ark and temple (Psalm 99:5, Heb. 8:4–5). If it’s acceptable to honor earthly images of heavenly realities, honoring icons of saints is also valid.
                </p>
                <p>
                    Orthodox Christians honor images to honor the people depicted. Early Christians did this too: Tertullian, Clement of Alexandria, and John Chrysostom all describe veneration of Christian symbols and figures.
                </p>
            </section>

            <section>
                <h2>Icons as Teaching Tools</h2>
                <p>
                    Icons teach the faith visually. One image can convey layers of meaning and entire stories. Even Jesus used visual aids—like the bronze serpent—to teach about his crucifixion (John 3:14–15).
                </p>
            </section>

            <section>
                <h2>Conclusion</h2>
                <p>
                    Objections to icons often misunderstand Scripture. The Orthodox tradition preserves the visual element of worship begun at Sinai. Icons are aids in worship, depicting heavenly realities. God has revealed his image in Christ, so it is appropriate to depict and honor him and the saints.
                </p>
                <p className="author-note">
                    Jakob Puckett is a reader in the Coptic Orthodox Church.
                </p>
            </section>
            <GoToTop/>
        </article>
    );
};

export default Icons;